import { useRef, useState, useCallback } from 'react';
import { Paperclip, Mic, SendHorizontal } from 'lucide-react';
import { cn } from '../lib/utils';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Called when the user clicks the mic icon. When provided, the mic button
   * becomes functional — clicking starts a voice call via the parent's
   * `useVoice` hook. When omitted, the mic is hidden (text-only embed).
   */
  onVoiceStart?: () => void;
}

export function ChatInput({ onSend, disabled, placeholder = 'Message...', onVoiceStart }: ChatInputProps) {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  const send = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    if (ref.current) ref.current.style.height = 'auto';
  }, [value, disabled, onSend]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const grow = () => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; }
  };

  const has = value.trim().length > 0;

  return (
    <div className="shrink-0 bg-background px-6 pb-4 pt-2 dark:bg-transparent">
      <div className="flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 dark:border-white/[0.1] dark:bg-white/[0.05]">
        <button type="button" className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground dark:text-white/30">
          <Paperclip className="size-4" strokeWidth={2} />
        </button>

        <textarea
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          onInput={grow}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="max-h-[120px] min-h-[32px] flex-1 resize-none self-center bg-transparent text-sm leading-8 text-foreground outline-none placeholder:text-muted-foreground/60 dark:text-white/90 dark:placeholder:text-white/25"
        />

        {onVoiceStart && (
          <button
            type="button"
            onClick={onVoiceStart}
            aria-label="Start voice call"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white/90"
          >
            <Mic className="size-4" strokeWidth={2} />
          </button>
        )}

        <button
          type="button"
          onClick={send}
          disabled={!has || disabled}
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full transition-all',
            has && !disabled
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground dark:bg-white/[0.1] dark:text-white/30',
          )}
        >
          <SendHorizontal className="size-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
